/**
 * The foundational game suite class with common behaviors and the full API interface.
 * This is implemented by the backend-backed GameSuite class, and the local-backed
 * HotseatGameSuite class.
 */
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
import { EventSubscriber } from '@a-type/utils';
import { gameSessionChatInitShape, isPrefixedId, Logger, LongGameError, } from '@long-game/common';
import { emptyGameDefinition, simpleError, } from '@long-game/game-definition';
import { produce } from 'immer';
import { action, autorun, computed, observable, runInAction, toJS } from 'mobx';
import { publicSdk } from '../api/index.js';
const ROOT_CHAT_SCENE_ID = 'null';
let LiveGameRoundSummary = (() => {
    var _a;
    let _raw_decorators;
    let _raw_initializers = [];
    let _raw_extraInitializers = [];
    let _roundIndex_decorators;
    let _roundIndex_initializers = [];
    let _roundIndex_extraInitializers = [];
    let _initialPlayerState_decorators;
    let _initialPlayerState_initializers = [];
    let _initialPlayerState_extraInitializers = [];
    let _yourTurnData_decorators;
    let _yourTurnData_initializers = [];
    let _yourTurnData_extraInitializers = [];
    let _turns_decorators;
    let _turns_initializers = [];
    let _turns_extraInitializers = [];
    return class LiveGameRoundSummary {
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            _raw_decorators = [(_a = observable).ref.bind(_a)];
            _roundIndex_decorators = [observable];
            _initialPlayerState_decorators = [observable];
            _yourTurnData_decorators = [observable];
            _turns_decorators = [observable];
            __esDecorate(this, null, _raw_decorators, { kind: "accessor", name: "raw", static: false, private: false, access: { has: obj => "raw" in obj, get: obj => obj.raw, set: (obj, value) => { obj.raw = value; } }, metadata: _metadata }, _raw_initializers, _raw_extraInitializers);
            __esDecorate(this, null, _roundIndex_decorators, { kind: "accessor", name: "roundIndex", static: false, private: false, access: { has: obj => "roundIndex" in obj, get: obj => obj.roundIndex, set: (obj, value) => { obj.roundIndex = value; } }, metadata: _metadata }, _roundIndex_initializers, _roundIndex_extraInitializers);
            __esDecorate(this, null, _initialPlayerState_decorators, { kind: "accessor", name: "initialPlayerState", static: false, private: false, access: { has: obj => "initialPlayerState" in obj, get: obj => obj.initialPlayerState, set: (obj, value) => { obj.initialPlayerState = value; } }, metadata: _metadata }, _initialPlayerState_initializers, _initialPlayerState_extraInitializers);
            __esDecorate(this, null, _yourTurnData_decorators, { kind: "accessor", name: "yourTurnData", static: false, private: false, access: { has: obj => "yourTurnData" in obj, get: obj => obj.yourTurnData, set: (obj, value) => { obj.yourTurnData = value; } }, metadata: _metadata }, _yourTurnData_initializers, _yourTurnData_extraInitializers);
            __esDecorate(this, null, _turns_decorators, { kind: "accessor", name: "turns", static: false, private: false, access: { has: obj => "turns" in obj, get: obj => obj.turns, set: (obj, value) => { obj.turns = value; } }, metadata: _metadata }, _turns_initializers, _turns_extraInitializers);
            if (_metadata) Object.defineProperty(this, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        #raw_accessor_storage = __runInitializers(this, _raw_initializers, void 0);
        get raw() { return this.#raw_accessor_storage; }
        set raw(value) { this.#raw_accessor_storage = value; }
        #roundIndex_accessor_storage = (__runInitializers(this, _raw_extraInitializers), __runInitializers(this, _roundIndex_initializers, void 0));
        get roundIndex() { return this.#roundIndex_accessor_storage; }
        set roundIndex(value) { this.#roundIndex_accessor_storage = value; }
        #initialPlayerState_accessor_storage = (__runInitializers(this, _roundIndex_extraInitializers), __runInitializers(this, _initialPlayerState_initializers, void 0));
        get initialPlayerState() { return this.#initialPlayerState_accessor_storage; }
        set initialPlayerState(value) { this.#initialPlayerState_accessor_storage = value; }
        #yourTurnData_accessor_storage = (__runInitializers(this, _initialPlayerState_extraInitializers), __runInitializers(this, _yourTurnData_initializers, void 0));
        get yourTurnData() { return this.#yourTurnData_accessor_storage; }
        set yourTurnData(value) { this.#yourTurnData_accessor_storage = value; }
        #turns_accessor_storage = (__runInitializers(this, _yourTurnData_extraInitializers), __runInitializers(this, _turns_initializers, void 0));
        get turns() { return this.#turns_accessor_storage; }
        set turns(value) { this.#turns_accessor_storage = value; }
        constructor(data) {
            __runInitializers(this, _turns_extraInitializers);
            this.roundIndex = data.roundIndex;
            this.initialPlayerState = data.initialPlayerState;
            this.yourTurnData = data.yourTurnData;
            this.turns = data.turns;
            this.raw = data;
        }
    };
})();
export { LiveGameRoundSummary };
let AbstractGameSuite = (() => {
    let _instanceExtraInitializers = [];
    let _localTurnData_decorators;
    let _localTurnData_initializers = [];
    let _localTurnData_extraInitializers = [];
    let _playerStatuses_decorators;
    let _playerStatuses_initializers = [];
    let _playerStatuses_extraInitializers = [];
    let _players_decorators;
    let _players_initializers = [];
    let _players_extraInitializers = [];
    let _sceneChats_decorators;
    let _sceneChats_initializers = [];
    let _sceneChats_extraInitializers = [];
    let _gameStatus_decorators;
    let _gameStatus_initializers = [];
    let _gameStatus_extraInitializers = [];
    let _rounds_decorators;
    let _rounds_initializers = [];
    let _rounds_extraInitializers = [];
    let _viewingRoundIndex_decorators;
    let _viewingRoundIndex_initializers = [];
    let _viewingRoundIndex_extraInitializers = [];
    let _latestRoundIndex_decorators;
    let _latestRoundIndex_initializers = [];
    let _latestRoundIndex_extraInitializers = [];
    let _postgameGlobalState_decorators;
    let _postgameGlobalState_initializers = [];
    let _postgameGlobalState_extraInitializers = [];
    let _gameId_decorators;
    let _gameId_initializers = [];
    let _gameId_extraInitializers = [];
    let _gameVersion_decorators;
    let _gameVersion_initializers = [];
    let _gameVersion_extraInitializers = [];
    let _nextRoundCheckAt_decorators;
    let _nextRoundCheckAt_initializers = [];
    let _nextRoundCheckAt_extraInitializers = [];
    let _turnSubmitTimeout_decorators;
    let _turnSubmitTimeout_initializers = [];
    let _turnSubmitTimeout_extraInitializers = [];
    let _submittingTurn_decorators;
    let _submittingTurn_initializers = [];
    let _submittingTurn_extraInitializers = [];
    let _remoteTurnError_decorators;
    let _remoteTurnError_initializers = [];
    let _remoteTurnError_extraInitializers = [];
    let _playerId_decorators;
    let _playerId_initializers = [];
    let _playerId_extraInitializers = [];
    let _members_decorators;
    let _members_initializers = [];
    let _members_extraInitializers = [];
    let _gameVotes_decorators;
    let _gameVotes_initializers = [];
    let _gameVotes_extraInitializers = [];
    let _pickingPlayer_decorators;
    let _pickingPlayer_initializers = [];
    let _pickingPlayer_extraInitializers = [];
    let _gameDefinition_decorators;
    let _gameDefinition_initializers = [];
    let _gameDefinition_extraInitializers = [];
    let _showRound_decorators;
    let _showRound_initializers = [];
    let _showRound_extraInitializers = [];
    let _get_viewingRound_decorators;
    let _get_latestRound_decorators;
    let _get_initialState_decorators;
    let _get_finalState_decorators;
    let _get_currentTurn_decorators;
    let _get_hasLocalTurn_decorators;
    let _get_playerStatus_decorators;
    let _get_winners_decorators;
    let _get_otherPlayers_decorators;
    let _get_viewingTurn_decorators;
    let _get_isViewingCurrentRound_decorators;
    let _get_turnWasSubmitted_decorators;
    let _get_isTurnSubmitDelayed_decorators;
    let _get_canSubmitTurn_decorators;
    let _get_turnError_decorators;
    let _get_chat_decorators;
    let _get_combinedLog_decorators;
    let _prepareTurn_decorators;
    let _prepareTurn_initializers = [];
    let _prepareTurn_extraInitializers = [];
    let _submitTurn_decorators;
    let _submitTurn_initializers = [];
    let _submitTurn_extraInitializers = [];
    let _wrappedSubmitTurn_decorators;
    let _wrappedSubmitTurn_initializers = [];
    let _wrappedSubmitTurn_extraInitializers = [];
    let _cancelSubmitTurn_decorators;
    let _cancelSubmitTurn_initializers = [];
    let _cancelSubmitTurn_extraInitializers = [];
    let _sendChat_decorators;
    let _sendChat_initializers = [];
    let _sendChat_extraInitializers = [];
    let _onChat_decorators;
    let _onChat_initializers = [];
    let _onChat_extraInitializers = [];
    let _onPlayerStatusChange_decorators;
    let _onPlayerStatusChange_initializers = [];
    let _onPlayerStatusChange_extraInitializers = [];
    let _onRoundChange_decorators;
    let _onRoundChange_initializers = [];
    let _onRoundChange_extraInitializers = [];
    let _onStatusChange_decorators;
    let _onStatusChange_initializers = [];
    let _onStatusChange_extraInitializers = [];
    let _onTurnPlayed_decorators;
    let _onTurnPlayed_initializers = [];
    let _onTurnPlayed_extraInitializers = [];
    let _onNextRoundScheduled_decorators;
    let _onNextRoundScheduled_initializers = [];
    let _onNextRoundScheduled_extraInitializers = [];
    let _onGameChange_decorators;
    let _onGameChange_initializers = [];
    let _onGameChange_extraInitializers = [];
    let _onMembersChange_decorators;
    let _onMembersChange_initializers = [];
    let _onMembersChange_extraInitializers = [];
    let _onPlayerVoteForGame_decorators;
    let _onPlayerVoteForGame_initializers = [];
    let _onPlayerVoteForGame_extraInitializers = [];
    let _loadPostgame_decorators;
    let _loadPostgame_initializers = [];
    let _loadPostgame_extraInitializers = [];
    let _get_localTurnStorageKey_decorators;
    let _setupLocalTurnStorage_decorators;
    let _setupLocalTurnStorage_initializers = [];
    let _setupLocalTurnStorage_extraInitializers = [];
    let _applyGameData_decorators;
    let _applyGameData_initializers = [];
    let _applyGameData_extraInitializers = [];
    let _changeGame_decorators;
    let _changeGame_initializers = [];
    let _changeGame_extraInitializers = [];
    let _loadMembers_decorators;
    let _loadMembers_initializers = [];
    let _loadMembers_extraInitializers = [];
    return class AbstractGameSuite {
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            _localTurnData_decorators = [observable];
            _playerStatuses_decorators = [observable];
            _players_decorators = [observable];
            _sceneChats_decorators = [observable];
            _gameStatus_decorators = [observable];
            _rounds_decorators = [observable];
            _viewingRoundIndex_decorators = [observable];
            _latestRoundIndex_decorators = [observable];
            _postgameGlobalState_decorators = [observable];
            _gameId_decorators = [observable];
            _gameVersion_decorators = [observable];
            _nextRoundCheckAt_decorators = [observable];
            _turnSubmitTimeout_decorators = [observable];
            _submittingTurn_decorators = [observable];
            _remoteTurnError_decorators = [observable];
            _playerId_decorators = [observable];
            _members_decorators = [observable];
            _gameVotes_decorators = [observable];
            _pickingPlayer_decorators = [observable];
            _gameDefinition_decorators = [observable];
            _showRound_decorators = [action];
            _get_viewingRound_decorators = [computed];
            _get_latestRound_decorators = [computed];
            _get_initialState_decorators = [computed];
            _get_finalState_decorators = [computed];
            _get_currentTurn_decorators = [computed];
            _get_hasLocalTurn_decorators = [computed];
            _get_playerStatus_decorators = [computed];
            _get_winners_decorators = [computed];
            _get_otherPlayers_decorators = [computed];
            _get_viewingTurn_decorators = [computed];
            _get_isViewingCurrentRound_decorators = [computed];
            _get_turnWasSubmitted_decorators = [computed];
            _get_isTurnSubmitDelayed_decorators = [computed];
            _get_canSubmitTurn_decorators = [computed];
            _get_turnError_decorators = [computed];
            _get_chat_decorators = [computed];
            _get_combinedLog_decorators = [computed];
            _prepareTurn_decorators = [action];
            _submitTurn_decorators = [action];
            _wrappedSubmitTurn_decorators = [action];
            _cancelSubmitTurn_decorators = [action];
            _sendChat_decorators = [action];
            _onChat_decorators = [action];
            _onPlayerStatusChange_decorators = [action];
            _onRoundChange_decorators = [action];
            _onStatusChange_decorators = [action];
            _onTurnPlayed_decorators = [action];
            _onNextRoundScheduled_decorators = [action];
            _onGameChange_decorators = [action];
            _onMembersChange_decorators = [action];
            _onPlayerVoteForGame_decorators = [action];
            _loadPostgame_decorators = [action];
            _get_localTurnStorageKey_decorators = [computed];
            _setupLocalTurnStorage_decorators = [action];
            _applyGameData_decorators = [action];
            _changeGame_decorators = [action];
            _loadMembers_decorators = [action];
            __esDecorate(this, null, _localTurnData_decorators, { kind: "accessor", name: "localTurnData", static: false, private: false, access: { has: obj => "localTurnData" in obj, get: obj => obj.localTurnData, set: (obj, value) => { obj.localTurnData = value; } }, metadata: _metadata }, _localTurnData_initializers, _localTurnData_extraInitializers);
            __esDecorate(this, null, _playerStatuses_decorators, { kind: "accessor", name: "playerStatuses", static: false, private: false, access: { has: obj => "playerStatuses" in obj, get: obj => obj.playerStatuses, set: (obj, value) => { obj.playerStatuses = value; } }, metadata: _metadata }, _playerStatuses_initializers, _playerStatuses_extraInitializers);
            __esDecorate(this, null, _players_decorators, { kind: "accessor", name: "players", static: false, private: false, access: { has: obj => "players" in obj, get: obj => obj.players, set: (obj, value) => { obj.players = value; } }, metadata: _metadata }, _players_initializers, _players_extraInitializers);
            __esDecorate(this, null, _sceneChats_decorators, { kind: "accessor", name: "sceneChats", static: false, private: false, access: { has: obj => "sceneChats" in obj, get: obj => obj.sceneChats, set: (obj, value) => { obj.sceneChats = value; } }, metadata: _metadata }, _sceneChats_initializers, _sceneChats_extraInitializers);
            __esDecorate(this, null, _gameStatus_decorators, { kind: "accessor", name: "gameStatus", static: false, private: false, access: { has: obj => "gameStatus" in obj, get: obj => obj.gameStatus, set: (obj, value) => { obj.gameStatus = value; } }, metadata: _metadata }, _gameStatus_initializers, _gameStatus_extraInitializers);
            __esDecorate(this, null, _rounds_decorators, { kind: "accessor", name: "rounds", static: false, private: false, access: { has: obj => "rounds" in obj, get: obj => obj.rounds, set: (obj, value) => { obj.rounds = value; } }, metadata: _metadata }, _rounds_initializers, _rounds_extraInitializers);
            __esDecorate(this, null, _viewingRoundIndex_decorators, { kind: "accessor", name: "viewingRoundIndex", static: false, private: false, access: { has: obj => "viewingRoundIndex" in obj, get: obj => obj.viewingRoundIndex, set: (obj, value) => { obj.viewingRoundIndex = value; } }, metadata: _metadata }, _viewingRoundIndex_initializers, _viewingRoundIndex_extraInitializers);
            __esDecorate(this, null, _latestRoundIndex_decorators, { kind: "accessor", name: "latestRoundIndex", static: false, private: false, access: { has: obj => "latestRoundIndex" in obj, get: obj => obj.latestRoundIndex, set: (obj, value) => { obj.latestRoundIndex = value; } }, metadata: _metadata }, _latestRoundIndex_initializers, _latestRoundIndex_extraInitializers);
            __esDecorate(this, null, _postgameGlobalState_decorators, { kind: "accessor", name: "postgameGlobalState", static: false, private: false, access: { has: obj => "postgameGlobalState" in obj, get: obj => obj.postgameGlobalState, set: (obj, value) => { obj.postgameGlobalState = value; } }, metadata: _metadata }, _postgameGlobalState_initializers, _postgameGlobalState_extraInitializers);
            __esDecorate(this, null, _gameId_decorators, { kind: "accessor", name: "gameId", static: false, private: false, access: { has: obj => "gameId" in obj, get: obj => obj.gameId, set: (obj, value) => { obj.gameId = value; } }, metadata: _metadata }, _gameId_initializers, _gameId_extraInitializers);
            __esDecorate(this, null, _gameVersion_decorators, { kind: "accessor", name: "gameVersion", static: false, private: false, access: { has: obj => "gameVersion" in obj, get: obj => obj.gameVersion, set: (obj, value) => { obj.gameVersion = value; } }, metadata: _metadata }, _gameVersion_initializers, _gameVersion_extraInitializers);
            __esDecorate(this, null, _nextRoundCheckAt_decorators, { kind: "accessor", name: "nextRoundCheckAt", static: false, private: false, access: { has: obj => "nextRoundCheckAt" in obj, get: obj => obj.nextRoundCheckAt, set: (obj, value) => { obj.nextRoundCheckAt = value; } }, metadata: _metadata }, _nextRoundCheckAt_initializers, _nextRoundCheckAt_extraInitializers);
            __esDecorate(this, null, _turnSubmitTimeout_decorators, { kind: "accessor", name: "turnSubmitTimeout", static: false, private: false, access: { has: obj => "turnSubmitTimeout" in obj, get: obj => obj.turnSubmitTimeout, set: (obj, value) => { obj.turnSubmitTimeout = value; } }, metadata: _metadata }, _turnSubmitTimeout_initializers, _turnSubmitTimeout_extraInitializers);
            __esDecorate(this, null, _submittingTurn_decorators, { kind: "accessor", name: "submittingTurn", static: false, private: false, access: { has: obj => "submittingTurn" in obj, get: obj => obj.submittingTurn, set: (obj, value) => { obj.submittingTurn = value; } }, metadata: _metadata }, _submittingTurn_initializers, _submittingTurn_extraInitializers);
            __esDecorate(this, null, _remoteTurnError_decorators, { kind: "accessor", name: "remoteTurnError", static: false, private: false, access: { has: obj => "remoteTurnError" in obj, get: obj => obj.remoteTurnError, set: (obj, value) => { obj.remoteTurnError = value; } }, metadata: _metadata }, _remoteTurnError_initializers, _remoteTurnError_extraInitializers);
            __esDecorate(this, null, _playerId_decorators, { kind: "accessor", name: "playerId", static: false, private: false, access: { has: obj => "playerId" in obj, get: obj => obj.playerId, set: (obj, value) => { obj.playerId = value; } }, metadata: _metadata }, _playerId_initializers, _playerId_extraInitializers);
            __esDecorate(this, null, _members_decorators, { kind: "accessor", name: "members", static: false, private: false, access: { has: obj => "members" in obj, get: obj => obj.members, set: (obj, value) => { obj.members = value; } }, metadata: _metadata }, _members_initializers, _members_extraInitializers);
            __esDecorate(this, null, _gameVotes_decorators, { kind: "accessor", name: "gameVotes", static: false, private: false, access: { has: obj => "gameVotes" in obj, get: obj => obj.gameVotes, set: (obj, value) => { obj.gameVotes = value; } }, metadata: _metadata }, _gameVotes_initializers, _gameVotes_extraInitializers);
            __esDecorate(this, null, _pickingPlayer_decorators, { kind: "accessor", name: "pickingPlayer", static: false, private: false, access: { has: obj => "pickingPlayer" in obj, get: obj => obj.pickingPlayer, set: (obj, value) => { obj.pickingPlayer = value; } }, metadata: _metadata }, _pickingPlayer_initializers, _pickingPlayer_extraInitializers);
            __esDecorate(this, null, _gameDefinition_decorators, { kind: "accessor", name: "gameDefinition", static: false, private: false, access: { has: obj => "gameDefinition" in obj, get: obj => obj.gameDefinition, set: (obj, value) => { obj.gameDefinition = value; } }, metadata: _metadata }, _gameDefinition_initializers, _gameDefinition_extraInitializers);
            __esDecorate(this, null, _get_viewingRound_decorators, { kind: "getter", name: "viewingRound", static: false, private: false, access: { has: obj => "viewingRound" in obj, get: obj => obj.viewingRound }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_latestRound_decorators, { kind: "getter", name: "latestRound", static: false, private: false, access: { has: obj => "latestRound" in obj, get: obj => obj.latestRound }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_initialState_decorators, { kind: "getter", name: "initialState", static: false, private: false, access: { has: obj => "initialState" in obj, get: obj => obj.initialState }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_finalState_decorators, { kind: "getter", name: "finalState", static: false, private: false, access: { has: obj => "finalState" in obj, get: obj => obj.finalState }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_currentTurn_decorators, { kind: "getter", name: "currentTurn", static: false, private: false, access: { has: obj => "currentTurn" in obj, get: obj => obj.currentTurn }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_hasLocalTurn_decorators, { kind: "getter", name: "hasLocalTurn", static: false, private: false, access: { has: obj => "hasLocalTurn" in obj, get: obj => obj.hasLocalTurn }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_playerStatus_decorators, { kind: "getter", name: "playerStatus", static: false, private: false, access: { has: obj => "playerStatus" in obj, get: obj => obj.playerStatus }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_winners_decorators, { kind: "getter", name: "winners", static: false, private: false, access: { has: obj => "winners" in obj, get: obj => obj.winners }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_otherPlayers_decorators, { kind: "getter", name: "otherPlayers", static: false, private: false, access: { has: obj => "otherPlayers" in obj, get: obj => obj.otherPlayers }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_viewingTurn_decorators, { kind: "getter", name: "viewingTurn", static: false, private: false, access: { has: obj => "viewingTurn" in obj, get: obj => obj.viewingTurn }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_isViewingCurrentRound_decorators, { kind: "getter", name: "isViewingCurrentRound", static: false, private: false, access: { has: obj => "isViewingCurrentRound" in obj, get: obj => obj.isViewingCurrentRound }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_turnWasSubmitted_decorators, { kind: "getter", name: "turnWasSubmitted", static: false, private: false, access: { has: obj => "turnWasSubmitted" in obj, get: obj => obj.turnWasSubmitted }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_isTurnSubmitDelayed_decorators, { kind: "getter", name: "isTurnSubmitDelayed", static: false, private: false, access: { has: obj => "isTurnSubmitDelayed" in obj, get: obj => obj.isTurnSubmitDelayed }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_canSubmitTurn_decorators, { kind: "getter", name: "canSubmitTurn", static: false, private: false, access: { has: obj => "canSubmitTurn" in obj, get: obj => obj.canSubmitTurn }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_turnError_decorators, { kind: "getter", name: "turnError", static: false, private: false, access: { has: obj => "turnError" in obj, get: obj => obj.turnError }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_chat_decorators, { kind: "getter", name: "chat", static: false, private: false, access: { has: obj => "chat" in obj, get: obj => obj.chat }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_combinedLog_decorators, { kind: "getter", name: "combinedLog", static: false, private: false, access: { has: obj => "combinedLog" in obj, get: obj => obj.combinedLog }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_localTurnStorageKey_decorators, { kind: "getter", name: "localTurnStorageKey", static: false, private: false, access: { has: obj => "localTurnStorageKey" in obj, get: obj => obj.localTurnStorageKey }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(null, null, _showRound_decorators, { kind: "field", name: "showRound", static: false, private: false, access: { has: obj => "showRound" in obj, get: obj => obj.showRound, set: (obj, value) => { obj.showRound = value; } }, metadata: _metadata }, _showRound_initializers, _showRound_extraInitializers);
            __esDecorate(null, null, _prepareTurn_decorators, { kind: "field", name: "prepareTurn", static: false, private: false, access: { has: obj => "prepareTurn" in obj, get: obj => obj.prepareTurn, set: (obj, value) => { obj.prepareTurn = value; } }, metadata: _metadata }, _prepareTurn_initializers, _prepareTurn_extraInitializers);
            __esDecorate(null, null, _submitTurn_decorators, { kind: "field", name: "submitTurn", static: false, private: false, access: { has: obj => "submitTurn" in obj, get: obj => obj.submitTurn, set: (obj, value) => { obj.submitTurn = value; } }, metadata: _metadata }, _submitTurn_initializers, _submitTurn_extraInitializers);
            __esDecorate(null, null, _wrappedSubmitTurn_decorators, { kind: "field", name: "wrappedSubmitTurn", static: false, private: false, access: { has: obj => "wrappedSubmitTurn" in obj, get: obj => obj.wrappedSubmitTurn, set: (obj, value) => { obj.wrappedSubmitTurn = value; } }, metadata: _metadata }, _wrappedSubmitTurn_initializers, _wrappedSubmitTurn_extraInitializers);
            __esDecorate(null, null, _cancelSubmitTurn_decorators, { kind: "field", name: "cancelSubmitTurn", static: false, private: false, access: { has: obj => "cancelSubmitTurn" in obj, get: obj => obj.cancelSubmitTurn, set: (obj, value) => { obj.cancelSubmitTurn = value; } }, metadata: _metadata }, _cancelSubmitTurn_initializers, _cancelSubmitTurn_extraInitializers);
            __esDecorate(null, null, _sendChat_decorators, { kind: "field", name: "sendChat", static: false, private: false, access: { has: obj => "sendChat" in obj, get: obj => obj.sendChat, set: (obj, value) => { obj.sendChat = value; } }, metadata: _metadata }, _sendChat_initializers, _sendChat_extraInitializers);
            __esDecorate(null, null, _onChat_decorators, { kind: "field", name: "onChat", static: false, private: false, access: { has: obj => "onChat" in obj, get: obj => obj.onChat, set: (obj, value) => { obj.onChat = value; } }, metadata: _metadata }, _onChat_initializers, _onChat_extraInitializers);
            __esDecorate(null, null, _onPlayerStatusChange_decorators, { kind: "field", name: "onPlayerStatusChange", static: false, private: false, access: { has: obj => "onPlayerStatusChange" in obj, get: obj => obj.onPlayerStatusChange, set: (obj, value) => { obj.onPlayerStatusChange = value; } }, metadata: _metadata }, _onPlayerStatusChange_initializers, _onPlayerStatusChange_extraInitializers);
            __esDecorate(null, null, _onRoundChange_decorators, { kind: "field", name: "onRoundChange", static: false, private: false, access: { has: obj => "onRoundChange" in obj, get: obj => obj.onRoundChange, set: (obj, value) => { obj.onRoundChange = value; } }, metadata: _metadata }, _onRoundChange_initializers, _onRoundChange_extraInitializers);
            __esDecorate(null, null, _onStatusChange_decorators, { kind: "field", name: "onStatusChange", static: false, private: false, access: { has: obj => "onStatusChange" in obj, get: obj => obj.onStatusChange, set: (obj, value) => { obj.onStatusChange = value; } }, metadata: _metadata }, _onStatusChange_initializers, _onStatusChange_extraInitializers);
            __esDecorate(null, null, _onTurnPlayed_decorators, { kind: "field", name: "onTurnPlayed", static: false, private: false, access: { has: obj => "onTurnPlayed" in obj, get: obj => obj.onTurnPlayed, set: (obj, value) => { obj.onTurnPlayed = value; } }, metadata: _metadata }, _onTurnPlayed_initializers, _onTurnPlayed_extraInitializers);
            __esDecorate(null, null, _onNextRoundScheduled_decorators, { kind: "field", name: "onNextRoundScheduled", static: false, private: false, access: { has: obj => "onNextRoundScheduled" in obj, get: obj => obj.onNextRoundScheduled, set: (obj, value) => { obj.onNextRoundScheduled = value; } }, metadata: _metadata }, _onNextRoundScheduled_initializers, _onNextRoundScheduled_extraInitializers);
            __esDecorate(null, null, _onGameChange_decorators, { kind: "field", name: "onGameChange", static: false, private: false, access: { has: obj => "onGameChange" in obj, get: obj => obj.onGameChange, set: (obj, value) => { obj.onGameChange = value; } }, metadata: _metadata }, _onGameChange_initializers, _onGameChange_extraInitializers);
            __esDecorate(null, null, _onMembersChange_decorators, { kind: "field", name: "onMembersChange", static: false, private: false, access: { has: obj => "onMembersChange" in obj, get: obj => obj.onMembersChange, set: (obj, value) => { obj.onMembersChange = value; } }, metadata: _metadata }, _onMembersChange_initializers, _onMembersChange_extraInitializers);
            __esDecorate(null, null, _onPlayerVoteForGame_decorators, { kind: "field", name: "onPlayerVoteForGame", static: false, private: false, access: { has: obj => "onPlayerVoteForGame" in obj, get: obj => obj.onPlayerVoteForGame, set: (obj, value) => { obj.onPlayerVoteForGame = value; } }, metadata: _metadata }, _onPlayerVoteForGame_initializers, _onPlayerVoteForGame_extraInitializers);
            __esDecorate(null, null, _loadPostgame_decorators, { kind: "field", name: "loadPostgame", static: false, private: false, access: { has: obj => "loadPostgame" in obj, get: obj => obj.loadPostgame, set: (obj, value) => { obj.loadPostgame = value; } }, metadata: _metadata }, _loadPostgame_initializers, _loadPostgame_extraInitializers);
            __esDecorate(null, null, _setupLocalTurnStorage_decorators, { kind: "field", name: "setupLocalTurnStorage", static: false, private: false, access: { has: obj => "setupLocalTurnStorage" in obj, get: obj => obj.setupLocalTurnStorage, set: (obj, value) => { obj.setupLocalTurnStorage = value; } }, metadata: _metadata }, _setupLocalTurnStorage_initializers, _setupLocalTurnStorage_extraInitializers);
            __esDecorate(null, null, _applyGameData_decorators, { kind: "field", name: "applyGameData", static: false, private: false, access: { has: obj => "applyGameData" in obj, get: obj => obj.applyGameData, set: (obj, value) => { obj.applyGameData = value; } }, metadata: _metadata }, _applyGameData_initializers, _applyGameData_extraInitializers);
            __esDecorate(null, null, _changeGame_decorators, { kind: "field", name: "changeGame", static: false, private: false, access: { has: obj => "changeGame" in obj, get: obj => obj.changeGame, set: (obj, value) => { obj.changeGame = value; } }, metadata: _metadata }, _changeGame_initializers, _changeGame_extraInitializers);
            __esDecorate(null, null, _loadMembers_decorators, { kind: "field", name: "loadMembers", static: false, private: false, access: { has: obj => "loadMembers" in obj, get: obj => obj.loadMembers, set: (obj, value) => { obj.loadMembers = value; } }, metadata: _metadata }, _loadMembers_initializers, _loadMembers_extraInitializers);
            if (_metadata) Object.defineProperty(this, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        instanceId = (__runInitializers(this, _instanceExtraInitializers), Math.random().toString(36).slice(2));
        #localTurnData_accessor_storage = __runInitializers(this, _localTurnData_initializers, void 0);
        get localTurnData() { return this.#localTurnData_accessor_storage; }
        set localTurnData(value) { this.#localTurnData_accessor_storage = value; }
        #playerStatuses_accessor_storage = (__runInitializers(this, _localTurnData_extraInitializers), __runInitializers(this, _playerStatuses_initializers, void 0));
        get playerStatuses() { return this.#playerStatuses_accessor_storage; }
        set playerStatuses(value) { this.#playerStatuses_accessor_storage = value; }
        #players_accessor_storage = (__runInitializers(this, _playerStatuses_extraInitializers), __runInitializers(this, _players_initializers, {}));
        get players() { return this.#players_accessor_storage; }
        set players(value) { this.#players_accessor_storage = value; }
        #sceneChats_accessor_storage = (__runInitializers(this, _players_extraInitializers), __runInitializers(this, _sceneChats_initializers, {
            [ROOT_CHAT_SCENE_ID]: { messages: [], nextToken: null, empty: false },
        }));
        get sceneChats() { return this.#sceneChats_accessor_storage; }
        set sceneChats(value) { this.#sceneChats_accessor_storage = value; }
        #gameStatus_accessor_storage = (__runInitializers(this, _sceneChats_extraInitializers), __runInitializers(this, _gameStatus_initializers, void 0));
        get gameStatus() { return this.#gameStatus_accessor_storage; }
        set gameStatus(value) { this.#gameStatus_accessor_storage = value; }
        #rounds_accessor_storage = (__runInitializers(this, _gameStatus_extraInitializers), __runInitializers(this, _rounds_initializers, {}));
        // rounds are keyed by player ID -- this is strange if you're thinking
        // about remote games, but it's important for hotseat where all players
        // are on the same device.
        get rounds() { return this.#rounds_accessor_storage; }
        set rounds(value) { this.#rounds_accessor_storage = value; }
        #viewingRoundIndex_accessor_storage = (__runInitializers(this, _rounds_extraInitializers), __runInitializers(this, _viewingRoundIndex_initializers, 0));
        get viewingRoundIndex() { return this.#viewingRoundIndex_accessor_storage; }
        set viewingRoundIndex(value) { this.#viewingRoundIndex_accessor_storage = value; }
        #latestRoundIndex_accessor_storage = (__runInitializers(this, _viewingRoundIndex_extraInitializers), __runInitializers(this, _latestRoundIndex_initializers, 0));
        get latestRoundIndex() { return this.#latestRoundIndex_accessor_storage; }
        set latestRoundIndex(value) { this.#latestRoundIndex_accessor_storage = value; }
        #postgameGlobalState_accessor_storage = (__runInitializers(this, _latestRoundIndex_extraInitializers), __runInitializers(this, _postgameGlobalState_initializers, null));
        get postgameGlobalState() { return this.#postgameGlobalState_accessor_storage; }
        set postgameGlobalState(value) { this.#postgameGlobalState_accessor_storage = value; }
        #gameId_accessor_storage = (__runInitializers(this, _postgameGlobalState_extraInitializers), __runInitializers(this, _gameId_initializers, void 0));
        get gameId() { return this.#gameId_accessor_storage; }
        set gameId(value) { this.#gameId_accessor_storage = value; }
        #gameVersion_accessor_storage = (__runInitializers(this, _gameId_extraInitializers), __runInitializers(this, _gameVersion_initializers, void 0));
        get gameVersion() { return this.#gameVersion_accessor_storage; }
        set gameVersion(value) { this.#gameVersion_accessor_storage = value; }
        #nextRoundCheckAt_accessor_storage = (__runInitializers(this, _gameVersion_extraInitializers), __runInitializers(this, _nextRoundCheckAt_initializers, null));
        get nextRoundCheckAt() { return this.#nextRoundCheckAt_accessor_storage; }
        set nextRoundCheckAt(value) { this.#nextRoundCheckAt_accessor_storage = value; }
        #turnSubmitTimeout_accessor_storage = (__runInitializers(this, _nextRoundCheckAt_extraInitializers), __runInitializers(this, _turnSubmitTimeout_initializers, null));
        get turnSubmitTimeout() { return this.#turnSubmitTimeout_accessor_storage; }
        set turnSubmitTimeout(value) { this.#turnSubmitTimeout_accessor_storage = value; }
        #submittingTurn_accessor_storage = (__runInitializers(this, _turnSubmitTimeout_extraInitializers), __runInitializers(this, _submittingTurn_initializers, false));
        get submittingTurn() { return this.#submittingTurn_accessor_storage; }
        set submittingTurn(value) { this.#submittingTurn_accessor_storage = value; }
        #remoteTurnError_accessor_storage = (__runInitializers(this, _submittingTurn_extraInitializers), __runInitializers(this, _remoteTurnError_initializers, null));
        get remoteTurnError() { return this.#remoteTurnError_accessor_storage; }
        set remoteTurnError(value) { this.#remoteTurnError_accessor_storage = value; }
        #playerId_accessor_storage = (__runInitializers(this, _remoteTurnError_extraInitializers), __runInitializers(this, _playerId_initializers, void 0));
        get playerId() { return this.#playerId_accessor_storage; }
        set playerId(value) { this.#playerId_accessor_storage = value; }
        #members_accessor_storage = (__runInitializers(this, _playerId_extraInitializers), __runInitializers(this, _members_initializers, []));
        get members() { return this.#members_accessor_storage; }
        set members(value) { this.#members_accessor_storage = value; }
        #gameVotes_accessor_storage = (__runInitializers(this, _members_extraInitializers), __runInitializers(this, _gameVotes_initializers, {}));
        // pregame stuff
        get gameVotes() { return this.#gameVotes_accessor_storage; }
        set gameVotes(value) { this.#gameVotes_accessor_storage = value; }
        #pickingPlayer_accessor_storage = (__runInitializers(this, _gameVotes_extraInitializers), __runInitializers(this, _pickingPlayer_initializers, false));
        // hotseat only
        get pickingPlayer() { return this.#pickingPlayer_accessor_storage; }
        set pickingPlayer(value) { this.#pickingPlayer_accessor_storage = value; }
        // static
        gameModules = __runInitializers(this, _pickingPlayer_extraInitializers);
        gameSessionId;
        #gameDefinition_accessor_storage = __runInitializers(this, _gameDefinition_initializers, emptyGameDefinition);
        get gameDefinition() { return this.#gameDefinition_accessor_storage; }
        set gameDefinition(value) { this.#gameDefinition_accessor_storage = value; }
        startedAt = (__runInitializers(this, _gameDefinition_extraInitializers), null);
        youAreLeader;
        timezone;
        events = new EventSubscriber();
        logger = new Logger('👾', 'game');
        // non-reactive
        disposes = [];
        constructor(init, { gameModules }) {
            // NOTE: how current player ID is assigned is up to the subclass
            this.gameSessionId = init.id;
            this.gameModules = gameModules;
            this.setupLocalTurnStorage();
            this.youAreLeader = init.youAreLeader;
            this.applyGameData(init);
            // chat is automatically sent on connection
            if (init.status.status === 'complete') {
                setTimeout(() => this.loadPostgame(), 0);
            }
        }
        dispose = () => this.disposes.forEach((dispose) => dispose());
        get subscribe() {
            return this.events.subscribe.bind(this.events);
        }
        /**
         * Internal usage. If the round summary for the current playerId
         * is available, returns it. Does not load it otherwise.
         */
        maybeGetRound = (roundIndex) => {
            return this.rounds[this.playerId]?.[roundIndex];
        };
        /**
         * Assigns the given round summary to the current playerId.
         */
        setRound = (round, playerId = this.playerId) => {
            if (!this.rounds[playerId]) {
                this.rounds[playerId] = [];
            }
            this.rounds[playerId][round.roundIndex] = new LiveGameRoundSummary(round);
        };
        // caching and suspense mechanisms which wrap loadRound
        cachedLoadRoundPromises = {};
        loadRoundSuspended = (roundIndex) => {
            throw this.loadRoundUnsuspended(roundIndex);
        };
        loadRoundUnsuspended = (roundIndex) => {
            if (this.gameStatus.status === 'pending') {
                throw new LongGameError(LongGameError.Code.Unknown, 'Cannot load rounds while the game is pending. Wait for the game to start.');
            }
            if (this.maybeGetRound(roundIndex)) {
                return;
            }
            return this.#getOrCreateRoundLoadingPromise(roundIndex);
        };
        #getOrCreateRoundLoadingPromise = (roundIndex) => {
            const cacheKey = `${this.playerId}-${roundIndex}`;
            if (!!this.cachedLoadRoundPromises[cacheKey]) {
                return this.cachedLoadRoundPromises[cacheKey];
            }
            const promise = this.loadRound(roundIndex)
                .then(action(this.setRound))
                .catch((err) => {
                this.logger.fatal('Error loading round', roundIndex, err);
                this.events.emit('error', new LongGameError(LongGameError.Code.Unknown, err.message));
                throw new LongGameError(LongGameError.Code.Unknown, err.message);
            })
                .finally(() => {
                this.logger.debug('loaded round', roundIndex);
            });
            this.cachedLoadRoundPromises[cacheKey] = promise;
            return promise;
        };
        /**
         * Get a summary of a specific round. If the round has not
         * been loaded from the server, this will suspend your
         * component.
         */
        getRound = (roundIndex) => {
            if (!this.maybeGetRound(roundIndex)) {
                // this will throw a promise if the round is not loaded
                this.loadRoundSuspended(roundIndex);
            }
            return this.maybeGetRound(roundIndex);
        };
        /**
         * Loads specific rounds by indexes and returns them.
         */
        getRounds = (indexes) => {
            const missingRounds = indexes.filter((i) => !this.maybeGetRound(i));
            if (missingRounds.length > 0) {
                // throw a meta-promise to load all rounds
                throw Promise.allSettled(missingRounds.map(this.#getOrCreateRoundLoadingPromise));
            }
            return indexes.map((i) => this.maybeGetRound(i));
        };
        /**
         * Loads a range of rounds from game history and returns them.
         */
        getRoundRange = (from, to) => {
            if (from < 0 || to < 0 || from > to) {
                throw new LongGameError(LongGameError.Code.BadRequest, `Invalid round range: ${from} to ${to}`);
            }
            const rounds = this.getRounds(Array.from({ length: to - from + 1 }, (_, i) => i + from));
            return rounds;
        };
        /**
         * Loads and returns the full game history. WARNING! This can be a
         * lot of data and network requests! Please don't use it?
         */
        getAllRounds = () => {
            return this.getRoundRange(0, this.latestRoundIndex);
        };
        showRound = __runInitializers(this, _showRound_initializers, (roundIndex) => {
            this.viewingRoundIndex = roundIndex;
        });
        /**
         * Which round is currently being viewed, according
         * to the user's position in history. The user can
         * use the UI to navigate forward and backward in
         * round history, so this is not necessarily the
         * latest round.
         * @see latestRound
         */
        get viewingRound() {
            return this.getRound(this.viewingRoundIndex);
        }
        /**
         * A summary of the most recent (i.e. current) round.
         * The turn data of this round may be incomplete:
         * players may not have submitted their turns yet.
         * Additionally, even public turn data from other players is not
         * accessible (null) since the round is in progress.
         */
        get latestRound() {
            return this.getRound(this.latestRoundIndex);
        }
        /**
         * The starting state for the currently viewed round, aka the final
         * state of the prior round.
         */
        get initialState() {
            return this.viewingRound.initialPlayerState;
        }
        /**
         * The final state for the currently viewed round.  When viewing the current round,
         * this is the initial state, since no turns have been played. After
         * your local turn has been played, this reflects the prospective
         * result of that turn (with no other player turns considered). Once the round
         * is resolved, it will be updated.
         */
        get finalState() {
            const { latestRoundIndex, viewingRoundIndex, currentTurn } = this;
            const viewingRound = this.getRound(viewingRoundIndex);
            if (viewingRoundIndex === latestRoundIndex) {
                // for current round, apply prospective turn to initial state
                if (!currentTurn)
                    return viewingRound.initialPlayerState;
                const newState = viewingRound.raw.initialPlayerState;
                return produce(newState, (draft) => {
                    this.gameDefinition.applyProspectiveTurnToPlayerState({
                        playerState: draft,
                        prospectiveTurn: {
                            data: toJS(currentTurn),
                            playerId: this.playerId,
                        },
                    });
                });
            }
            const nextRound = this.getRound(viewingRoundIndex + 1);
            // since historical rounds need to be loaded, we may not have the next
            // round available, theoretically. Implementation should prevent this
            // from actually being rendered, but either way, we should handle it.
            // Presently, we just return the initial state.
            if (!nextRound) {
                return this.initialState;
            }
            return nextRound.initialPlayerState;
        }
        get currentTurn() {
            const { localTurnData: localTurn, latestRound } = this;
            const remoteTurn = latestRound.yourTurnData;
            if (localTurn)
                return localTurn;
            if (localTurn === null) {
                // if localTurnData is null, it means the user has explicitly cleared
                // their turn data, so we should not return the remote turn.
                return this.gameDefinition.getInitialTurn?.() ?? null;
            }
            if (remoteTurn)
                return remoteTurn;
            return this.gameDefinition.getInitialTurn?.() ?? null;
        }
        get hasLocalTurn() {
            return !!this.localTurnData;
        }
        get playerStatus() {
            return this.playerStatuses[this.playerId];
        }
        get winners() {
            if (this.gameStatus.status !== 'complete') {
                return [];
            }
            return (this.gameStatus.winnerIds?.map((winnerId) => {
                const member = this.members.find((m) => m.id === winnerId);
                if (!member) {
                    this.logger.warn(`Winner with ID ${winnerId} not found among members`);
                    return { id: winnerId, displayName: '???', color: 'gray' };
                }
                return member;
            }) ?? []);
        }
        get otherPlayers() {
            return this.members.filter((m) => m.id !== this.playerId);
        }
        /**
         * If the viewed round is the active round, this will
         * include a locally drafted turn. Otherwise, it will
         * show your turn from the historical round, if you had one.
         */
        get viewingTurn() {
            const { viewingRound, localTurnData, latestRound } = this;
            if (viewingRound.roundIndex === latestRound.roundIndex) {
                return localTurnData ?? viewingRound.yourTurnData;
            }
            return viewingRound.yourTurnData;
        }
        get isViewingCurrentRound() {
            return this.viewingRoundIndex === this.latestRoundIndex;
        }
        get turnWasSubmitted() {
            return !!this.latestRound.yourTurnData;
        }
        get isTurnSubmitDelayed() {
            return !!this.turnSubmitTimeout;
        }
        /**
         * Even if the player submitted a turn for this round,
         * this will tell you if they've made changes since and
         * need to resubmit to the server.
         */
        get canSubmitTurn() {
            return !!this.localTurnData && !this.turnError;
        }
        get turnError() {
            if (this.localTurnData === undefined)
                return null;
            if (this.localTurnData === null) {
                // user has manually reset turn. if no turn was submitted,
                // we're still in an 'initial' state and no error is needed.
                if (!this.turnWasSubmitted) {
                    return null;
                }
                // otherwise we should validate the reset turn.
                const toValidate = this.gameDefinition.getInitialTurn?.() ?? null;
                if (!toValidate) {
                    return simpleError('You submitted an empty turn');
                }
                const err = this.validateTurn(toValidate);
                if (typeof err === 'string') {
                    return simpleError(err);
                }
                return err;
            }
            const err = this.validateTurn(this.localTurnData) || null;
            if (typeof err === 'string') {
                return simpleError(err);
            }
            return err;
        }
        getPreviousTurnForUpdater = (__runInitializers(this, _showRound_extraInitializers), () => {
            if (!this.gameDefinition.getInitialTurn) {
                return this.localTurnData ? toJS(this.localTurnData) : null;
            }
            return (toJS(this.localTurnData) || this.gameDefinition.getInitialTurn());
        });
        validatePartialTurn = (turnData) => {
            if (!this.gameDefinition.validatePartialTurn)
                return this.validateTurn(turnData);
            const baseState = toJS(this.latestRound.initialPlayerState);
            const roundIndex = this.latestRound.roundIndex;
            const dataToValidate = typeof turnData === 'function'
                ? turnData(this.getPreviousTurnForUpdater())
                : turnData;
            if (!dataToValidate) {
                return null;
            }
            const err = this.gameDefinition.validatePartialTurn({
                members: this.members,
                playerState: baseState,
                roundIndex,
                turn: {
                    playerId: this.playerId,
                    data: dataToValidate,
                },
            });
            if (err) {
                if (typeof err === 'string') {
                    return simpleError(err);
                }
                return err;
            }
            return null;
        };
        /**
         * Validate a potential turn without applying it to the client.
         * You can derive your new turn from the existing data with a function parameter.
         */
        validateTurn = (turnData) => {
            const baseState = toJS(this.latestRound.initialPlayerState);
            const roundIndex = this.latestRound.roundIndex;
            const dataToValidate = typeof turnData === 'function'
                ? turnData(this.getPreviousTurnForUpdater())
                : turnData;
            if (!dataToValidate) {
                return null; // no turn data to validate
            }
            const params = {
                members: this.members,
                playerState: baseState,
                roundIndex,
                turn: {
                    playerId: this.playerId,
                    data: dataToValidate,
                },
            };
            const err = this.gameDefinition.validatePartialTurn?.(params) ||
                this.gameDefinition.validateTurn(params);
            if (err) {
                if (typeof err === 'string') {
                    return simpleError(err);
                }
                return err;
            }
            return null;
        };
        get chat() {
            return this.sceneChats.null.messages;
        }
        // this may seem superfluous but in hotseat mode
        // we want to hide dms that aren't yours and there's
        // not any point in doing something more elaborate!
        filterChats = (msg) => {
            return !msg.recipientIds || msg.recipientIds.includes(this.playerId);
        };
        getSceneChat(sceneId) {
            return (this.sceneChats[sceneId] ?? {
                messages: [],
            }).messages.filter(this.filterChats);
        }
        get combinedLog() {
            const chat = this.chat;
            // chat is grouped by round first, then ordered by timestamp.
            // this may seem a little unintuitive, but the assigned round
            // of a chat may be a future round compared to when the chat
            // was created, and the chat will not be shown until that round
            // is reached, so round index is the primary ordering.
            // although the client may be viewing a different round, we
            // always return all logs. The UI can decide how to display
            // "future" logs when the user is viewing history.
            const currentRoundIndex = this.latestRoundIndex;
            // additionally, this grouping allows us to insert rounds into the log
            // in the right spot, since we don't have timestamps for them by nature.
            const chatsGroupedByRound = chat.reduce((acc, msg) => {
                const roundIndex = msg.roundIndex;
                if (!acc.has(roundIndex)) {
                    acc.set(roundIndex, []);
                }
                // we rely on chat already being sorted by timestamp
                acc.get(roundIndex).push(msg);
                return acc;
            }, new Map());
            const log = new Array();
            for (let roundIndex = 0; roundIndex <= currentRoundIndex; roundIndex++) {
                const chatForRound = chatsGroupedByRound.get(roundIndex);
                if (chatForRound) {
                    log.push(...chatForRound
                        .filter(this.filterChats)
                        .map((msg) => ({
                        type: 'chat',
                        chatMessage: msg,
                        timestamp: msg.createdAt,
                    }))
                        .sort((a, b) => new Date(a.timestamp) > new Date(b.timestamp) ? 1 : -1));
                }
                if (roundIndex < currentRoundIndex) {
                    log.push({
                        type: 'round',
                        roundIndex,
                    });
                }
            }
            // only add postgame if the game is completed
            if (this.gameStatus.status === 'complete') {
                log.push(...(chatsGroupedByRound
                    .get(-1)
                    ?.filter(this.filterChats)
                    ?.map((msg) => ({
                    type: 'chat',
                    chatMessage: msg,
                    timestamp: msg.createdAt,
                })) ?? []).sort((a, b) => new Date(a.timestamp) > new Date(b.timestamp) ? 1 : -1));
            }
            return log;
        }
        getPlayer = (id) => {
            return (this.players[id] ?? {
                id,
                displayName: 'Loading...',
                imageUrl: null,
                color: 'gray',
            });
        };
        prepareTurn = __runInitializers(this, _prepareTurn_initializers, (turn) => {
            if (typeof turn === 'function') {
                this.localTurnData = turn(this.getPreviousTurnForUpdater());
            }
            else {
                this.localTurnData = turn;
            }
            // if a turn submit was delayed, cancel it
            if (this.turnSubmitTimeout) {
                clearTimeout(this.turnSubmitTimeout);
                this.turnSubmitTimeout = null;
            }
            // if we had a remote turn error, clear it
            if (this.remoteTurnError) {
                this.remoteTurnError = null;
            }
            this.events.emit('turnPrepared');
        });
        submitTurn = (__runInitializers(this, _prepareTurn_extraInitializers), __runInitializers(this, _submitTurn_initializers, async ({ data: override, delay = 5000, } = {}) => {
            if (override) {
                this.prepareTurn(override);
            }
            const localTurnData = this.localTurnData;
            if (!localTurnData) {
                return simpleError('Play a turn first!');
            }
            const error = this.turnError;
            if (error) {
                this.events.emit('turnValidationFailed', error);
                return error;
            }
            if (this.turnSubmitTimeout) {
                clearTimeout(this.turnSubmitTimeout);
                this.turnSubmitTimeout = null;
            }
            // capture current player ID - in hotseat this could change during the delay
            const playerId = this.playerId;
            if (delay) {
                this.events.emit('turnSubmitDelayed', delay);
                return new Promise((resolve) => {
                    // if the user closes the window before the timeout, force the submit.
                    const beforeUnload = async () => {
                        if (this.turnSubmitTimeout) {
                            clearTimeout(this.turnSubmitTimeout);
                            this.turnSubmitTimeout = null;
                            await this.wrappedSubmitTurn(localTurnData, playerId);
                            resolve();
                        }
                    };
                    window.addEventListener('beforeunload', beforeUnload, {
                        once: true,
                    });
                    const visibilityChange = () => {
                        if (document.visibilityState === 'hidden') {
                            beforeUnload();
                        }
                    };
                    document.addEventListener('visibilitychange', visibilityChange, {
                        once: true,
                    });
                    runInAction(() => {
                        this.turnSubmitTimeout = setTimeout(async () => {
                            window.removeEventListener('beforeunload', beforeUnload);
                            document.removeEventListener('visibilitychange', visibilityChange);
                            await this.wrappedSubmitTurn(localTurnData, playerId);
                            resolve();
                        }, delay);
                    });
                });
            }
            else {
                return this.wrappedSubmitTurn(localTurnData, playerId);
            }
        }));
        wrappedSubmitTurn = (__runInitializers(this, _submitTurn_extraInitializers), __runInitializers(this, _wrappedSubmitTurn_initializers, async (data, playerId) => {
            if (this.turnSubmitTimeout) {
                clearTimeout(this.turnSubmitTimeout);
                this.turnSubmitTimeout = null;
            }
            this.submittingTurn = true;
            const submittingToRound = this.latestRoundIndex;
            try {
                const remoteError = await this.actuallySubmitTurn(toJS(data), playerId);
                if (remoteError) {
                    this.logger.info(`Turn submission invalid: ${remoteError.message}`);
                    this.remoteTurnError = remoteError;
                }
                runInAction(() => {
                    if (this.maybeGetRound(submittingToRound)) {
                        this.rounds[this.playerId][submittingToRound].yourTurnData = data;
                    }
                    this.localTurnData = undefined;
                    this.remoteTurnError = null;
                });
                this.events.emit('turnPlayed');
            }
            catch (e) {
                const msg = LongGameError.wrap(e).message;
                this.logger.fatal(e);
                this.events.emit('error', new LongGameError(LongGameError.Code.Unknown, msg));
                runInAction(() => {
                    this.remoteTurnError = simpleError(msg);
                });
            }
            finally {
                runInAction(() => {
                    this.submittingTurn = false;
                });
            }
        }));
        cancelSubmitTurn = (__runInitializers(this, _wrappedSubmitTurn_extraInitializers), __runInitializers(this, _cancelSubmitTurn_initializers, () => {
            if (this.turnSubmitTimeout) {
                clearTimeout(this.turnSubmitTimeout);
                this.turnSubmitTimeout = null;
            }
            this.events.emit('turnSubmitCancelled');
        }));
        sendChat = (__runInitializers(this, _cancelSubmitTurn_extraInitializers), __runInitializers(this, _sendChat_initializers, async (message) => {
            const messageWithRound = {
                ...message,
                roundIndex: message.roundIndex === undefined
                    ? this.latestRoundIndex
                    : message.roundIndex,
                authorId: this.playerId,
            };
            const validated = gameSessionChatInitShape.safeParse(messageWithRound);
            if (!validated.success) {
                this.logger.urgent('Invalid chat message', validated.error);
                this.events.emit('error', new LongGameError(LongGameError.Code.BadRequest, 'Invalid chat message: ' + validated.error.message));
                return;
            }
            await this.actuallySendChat(validated.data);
        }));
        loadMoreChat = (__runInitializers(this, _sendChat_extraInitializers), async (sceneId) => {
            const sceneChat = this.sceneChats[sceneId ?? ROOT_CHAT_SCENE_ID];
            if (sceneChat?.empty) {
                this.logger.debug('No more chat to load for scene', sceneId);
                return;
            }
            this.actuallyLoadMoreChat(sceneChat?.nextToken, sceneId);
        });
        onChat = __runInitializers(this, _onChat_initializers, (msg) => {
            let sceneChat = this.sceneChats[msg.sceneId ?? ROOT_CHAT_SCENE_ID];
            if (!sceneChat) {
                sceneChat = this.sceneChats[msg.sceneId ?? ROOT_CHAT_SCENE_ID] = {
                    messages: [],
                    nextToken: null,
                    empty: false,
                };
            }
            msg.messages.forEach((msg) => {
                sceneChat.messages = sceneChat.messages.filter((c) => c.id !== msg.id);
                sceneChat.messages.push(msg);
                sceneChat.messages.sort((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1);
            });
            if (msg.nextToken !== undefined) {
                sceneChat.nextToken = msg.nextToken;
            }
        });
        onPlayerStatusChange = (__runInitializers(this, _onChat_extraInitializers), __runInitializers(this, _onPlayerStatusChange_initializers, (msg) => {
            this.playerStatuses[msg.playerId] = {
                ...this.playerStatuses[msg.playerId],
                ...msg.playerStatus,
            };
        }));
        onRoundChange = (__runInitializers(this, _onPlayerStatusChange_extraInitializers), __runInitializers(this, _onRoundChange_initializers, async (msg) => {
            await this.loadRoundUnsuspended(msg.newRoundIndex);
            runInAction(() => {
                this.latestRoundIndex = msg.newRoundIndex;
                // reset turn data for new round
                this.localTurnData = undefined;
                // update current state if the round has advanced and we were viewing
                // the current round
                if (this.viewingRoundIndex === msg.newRoundIndex - 1) {
                    this.viewingRoundIndex = msg.newRoundIndex;
                }
                // update player statuses
                for (const [id, status] of Object.entries(msg.playerStatuses)) {
                    if (status && isPrefixedId(id, 'u')) {
                        this.playerStatuses[id] = status;
                    }
                }
            });
            this.events.emit('roundChanged');
        }));
        onStatusChange = (__runInitializers(this, _onRoundChange_extraInitializers), __runInitializers(this, _onStatusChange_initializers, (msg) => {
            this.gameStatus = msg.status;
            // prefetch postgame when status is completed
            if (msg.status.status === 'complete') {
                this.loadPostgame();
            }
        }));
        onTurnPlayed = (__runInitializers(this, _onStatusChange_extraInitializers), __runInitializers(this, _onTurnPlayed_initializers, (msg) => {
            // update the round with the new turn data
            const round = this.maybeGetRound(msg.roundIndex);
            if (round) {
                // replace the turn data for the player
                const playerId = msg.turn.playerId;
                const turnData = msg.turn.data;
                const playerIndex = round.turns.findIndex((t) => t.playerId === playerId);
                if (playerIndex === -1) {
                    round.turns.push({ playerId, data: turnData });
                }
                else {
                    round.turns[playerIndex] = { playerId, data: turnData };
                }
            }
        }));
        onNextRoundScheduled = (__runInitializers(this, _onTurnPlayed_extraInitializers), __runInitializers(this, _onNextRoundScheduled_initializers, (msg) => {
            this.nextRoundCheckAt = new Date(msg.nextRoundCheckAt);
        }));
        onGameChange = (__runInitializers(this, _onNextRoundScheduled_extraInitializers), __runInitializers(this, _onGameChange_initializers, async () => {
            const newData = await this.getGameData();
            this.applyGameData(newData);
        }));
        onMembersChange = (__runInitializers(this, _onGameChange_extraInitializers), __runInitializers(this, _onMembersChange_initializers, async (msg) => {
            this.members = msg.members;
            this.players = msg.members.reduce((acc, member) => {
                acc[member.id] = this.players[member.id] ?? {
                    id: member.id,
                    displayName: member.displayName,
                    imageUrl: null,
                    color: member.color,
                };
                return acc;
            }, {});
            await this.loadMembers();
        }));
        onPlayerVoteForGame = (__runInitializers(this, _onMembersChange_extraInitializers), __runInitializers(this, _onPlayerVoteForGame_initializers, (msg) => {
            this.gameVotes = msg.votes;
        }));
        onGameStarting = (__runInitializers(this, _onPlayerVoteForGame_extraInitializers), (msg) => {
            this.events.emit('gameStarting', msg.startsAt);
        });
        loadPostgame = __runInitializers(this, _loadPostgame_initializers, async () => {
            const postgame = await this.getPostgame();
            if (postgame) {
                this.postgameGlobalState = postgame;
            }
        });
        get localTurnStorageKey() {
            return `game-session-${this.gameSessionId}-local-turn-${this.playerId}`;
        }
        setupLocalTurnStorage = (__runInitializers(this, _loadPostgame_extraInitializers), __runInitializers(this, _setupLocalTurnStorage_initializers, () => {
            // when the key changes, load new localTurnData
            autorun(() => {
                const localTurn = localStorage.getItem(this.localTurnStorageKey);
                runInAction(() => {
                    if (localTurn && localTurn !== 'undefined') {
                        this.localTurnData = JSON.parse(localTurn);
                    }
                    else {
                        this.localTurnData = undefined;
                    }
                });
            });
            // when localTurnData changes, save it to localStorage
            autorun(() => {
                if (this.localTurnData === undefined) {
                    localStorage.removeItem(this.localTurnStorageKey);
                }
                else {
                    localStorage.setItem(this.localTurnStorageKey, JSON.stringify(this.localTurnData));
                }
            });
        }));
        applyGameData = (__runInitializers(this, _setupLocalTurnStorage_extraInitializers), __runInitializers(this, _applyGameData_initializers, async (init) => {
            this.gameId = init.gameId || 'empty';
            // immediately attempt to resolve a real id
            if (init.gameId) {
                publicSdk.getResolvedGameIdFromAlias.run({ aliasId: init.gameId }).then(action((id) => {
                    this.gameId = id;
                    this.gameModules.getGameDefinition(id, gameVersion).then(action((gameDefinition) => {
                        this.gameDefinition = gameDefinition;
                    }));
                }));
            }
            else {
                this.gameDefinition = emptyGameDefinition;
            }
            const gameVersion = init.gameVersion || 'v1';
            this.viewingRoundIndex = init.currentRoundIndex;
            this.latestRoundIndex = init.currentRoundIndex;
            this.localTurnData = undefined;
            this.playerStatuses = init.playerStatuses;
            this.gameVersion = gameVersion;
            this.members = init.members;
            this.gameStatus = init.status;
            this.startedAt = init.startedAt ? new Date(init.startedAt) : null;
            this.timezone = init.timezone;
            this.players = init.members.reduce((acc, member) => {
                acc[member.id] = {
                    id: member.id,
                    displayName: member.displayName,
                    color: member.color,
                };
                return acc;
            }, {});
            this.nextRoundCheckAt = init.nextRoundCheckAt
                ? new Date(init.nextRoundCheckAt)
                : null;
            if (init.gameVotes) {
                this.gameVotes = init.gameVotes;
            }
            // wait a tick... since this gets called from the constructor...
            // subclasses haven't defined methods yet :S
            setTimeout(this.loadMembers);
        }));
        changeGame = (__runInitializers(this, _applyGameData_extraInitializers), __runInitializers(this, _changeGame_initializers, async (gameId, version) => {
            if (this.gameStatus.status !== 'pending') {
                throw new LongGameError(LongGameError.Code.BadRequest, 'Cannot change game when the game is already started.');
            }
            this.gameDefinition = (await this.gameModules.getGameDefinition(gameId, version));
            this.gameId = gameId;
            this.gameVersion = version;
        }));
        loadMembers = (__runInitializers(this, _changeGame_extraInitializers), __runInitializers(this, _loadMembers_initializers, async () => {
            const members = await this.getMembers();
            members.forEach(action((member) => {
                if (!this.players[member.id]) {
                    this.players[member.id] = member;
                }
                else {
                    // selectively override from API - we want to keep player colors intact,
                    // particularly. but we can show the latest display name.
                    this.players[member.id].displayName ||= member.displayName;
                }
            }));
            this.events.emit('membersChanged');
        }));
        debug = (__runInitializers(this, _loadMembers_extraInitializers), async () => {
            // try loading global state (works in dev mode)
            try {
                await this.loadPostgame();
            }
            catch (e) {
                this.logger.fatal('Error loading global postgame state. Are you running with DEV_MODE flag on backend?', e);
            }
            const debugValue = {
                ...toJS(this),
                // toJS doesn't evaluate computed properties, so we need to
                // do this manually.
                latestRound: toJS(this.latestRound),
                initialState: toJS(this.initialState),
                finalState: toJS(this.finalState),
                currentTurn: toJS(this.currentTurn),
                viewingTurn: toJS(this.viewingTurn),
                globalState: toJS(this.postgameGlobalState),
                turns: await this.getDevModeTurns(),
                viewingRound: toJS(this.viewingRound),
                rounds: toJS(this.rounds),
            };
            // remove functions
            for (const key in debugValue) {
                if (typeof debugValue[key] === 'function') {
                    delete debugValue[key];
                }
            }
            // add helpers
            debugValue.getRoundIndex = () => this.gameDefinition.getRoundIndex({
                currentTime: new Date(),
                startedAt: new Date(this.startedAt),
                gameTimeZone: this.timezone,
                members: debugValue.members,
                globalState: debugValue.globalState,
                turns: debugValue.turns,
                environment: 'development',
            });
            debugValue.resetGame = () => {
                this.resetGame();
            };
            window.gameSuiteRaw = debugValue;
            return debugValue;
        });
    };
})();
export { AbstractGameSuite };
//# sourceMappingURL=AbstractGameSuite.js.map